import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Filter, Search, List } from 'lucide-react';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { getFieldOptionsSync, isSelectField } from '../../hooks/useFieldOptions';

/**
 * Advanced Entity Filter Component
 * 
 * A reusable filter component that works with any entity configuration.
 * Supports:
 * - All entity fields (including custom fields)
 * - Custom table fields with column selection (fieldname.column)
 * - Choice/select fields with dropdown options
 * - Multiple operators based on data type
 * - Multiple filter rules
 * 
 * @param {string} entityName - Entity name (e.g., 'customer', 'lead')
 * @param {array} filters - Current filter state array
 * @param {function} onFiltersChange - Callback when filters change
 * @param {object} options - Additional options
 */
const EntityFilter = ({ 
  entityName, 
  filters = [], 
  onFiltersChange,
  options = {},
  customerType = null // 'Person' or 'Company' for customer entity
}) => {
  const { t, i18n } = useTranslation();
  const { config: entityConfig } = useEntityConfig(entityName);
  const [localFilters, setLocalFilters] = useState(filters);
  const [showValueSelector, setShowValueSelector] = useState(null); // { filterId, field }
  const [availableValues, setAvailableValues] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const isUpdatingRef = useRef(false); // Track if we're updating filters internally

  // Check if operator requires a value (defined early for use in useEffect)
  const operatorRequiresValue = (operator) => {
    return !['isNull', 'isNotNull'].includes(operator);
  };

  // Sync local state with prop changes, but preserve incomplete filters
  useEffect(() => {
    // Skip sync if we're in the middle of updating filters internally
    if (isUpdatingRef.current) {
      return;
    }
    
    // Check if we have incomplete filters in local state
    const hasIncompleteFilters = localFilters.some(f => {
      if (!f.field || !f.operator) return true;
      if (operatorRequiresValue(f.operator)) {
        return f.value === undefined || f.value === '' || f.value === null;
      }
      return false;
    });
    
    // If we have incomplete filters, don't sync from props
    // This allows users to fill in new filters without them disappearing
    if (hasIncompleteFilters) {
      return;
    }
    
    // No incomplete filters - safe to sync from props
    // Only clear if props are explicitly empty
    if (filters.length === 0) {
      if (localFilters.length > 0) {
        setLocalFilters([]);
      }
      return;
    }
    
    // Deep compare to avoid unnecessary updates
    const filtersChanged = JSON.stringify(localFilters) !== JSON.stringify(filters);
    if (filtersChanged) {
      setLocalFilters(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Get all filterable fields (including custom fields)
  const filterableFields = useMemo(() => {
    if (!entityConfig || !entityConfig.fields) return [];
    
    const fields = [];
    const isThaiLanguage = i18n?.language?.toLowerCase().startsWith('th') || false;
    
    Object.entries(entityConfig.fields).forEach(([fieldName, fieldConfig]) => {
      // Skip the raw Attributes field - it stores custom fields as JSON
      if (fieldName === 'Attributes') {
        return;
      }
      
      // Skip hidden fields and auto-generated IDs (except SalespersonID which we want to include)
      if (fieldConfig.import?.enabled === false || 
          fieldConfig.validation?.autoGenerated ||
          (fieldName.endsWith('ID') && fieldName !== entityConfig.entity?.primaryKey && fieldName !== 'SalespersonID')) {
        return;
      }
      
      // Filter by customer type if specified (for customer entity)
      if (customerType && entityName === 'customer') {
        const visibleWhen = fieldConfig.ui?.visibleWhen;
        if (visibleWhen) {
          // Check if field should be visible for this customer type
          if (visibleWhen.includes(`CustomerType = 'Person'`) && customerType !== 'Person') {
            return;
          }
          if (visibleWhen.includes(`CustomerType = 'Company'`) && customerType !== 'Company') {
            return;
          }
        }
        
        // Also check database table - Person fields for Person tab, Company fields for Company tab
        const dbTable = fieldConfig.database?.table;
        if (customerType === 'Person' && dbTable === 'Companies' && fieldName !== 'PersonCompanyID') {
          return; // Skip company-only fields on person tab
        }
        if (customerType === 'Company' && dbTable === 'Persons') {
          return; // Skip person-only fields on company tab
        }
      }
      
      const displayName = isThaiLanguage && fieldConfig.displayNameTH 
        ? fieldConfig.displayNameTH 
        : (fieldConfig.displayName || fieldName);
      
      // Check if it's a custom table field
      const isTableField = fieldConfig.metadata?.attributeType === 'table' || 
                          (fieldConfig.storage?.type === 'attributes' && 
                           (fieldConfig.ui?.tableColumns || fieldConfig.ui?.columns || fieldConfig.columns));
      
      if (isTableField) {
        // Add table field with columns - check multiple locations for columns definition
        const tableColumns = fieldConfig.ui?.tableColumns || fieldConfig.ui?.columns || fieldConfig.columns || [];
        tableColumns.forEach(column => {
          // Exclude calculation columns
          if (column.type === 'calculated') return;
          
          fields.push({
            value: `${fieldName}.${column.key}`,
            label: `${displayName} - ${column.label || column.key}`,
            fieldName,
            columnKey: column.key,
            dataType: column.type || 'text',
            fieldConfig,
            isTableColumn: true // Flag for special handling in backend query
          });
        });
      } else {
        // Special handling for SalespersonID - treat as text field to filter by name
        if (fieldName === 'SalespersonID') {
          fields.push({
            value: 'SalespersonName', // Use SalespersonName for filtering
            label: displayName,
            fieldName: 'SalespersonName', // Backend will join with Users table
            dataType: 'string', // Treat as string to search by name
            fieldConfig: {
              ...fieldConfig,
              dataType: 'string',
              isSalesperson: true // Flag for special handling
            }
          });
        } else {
          // Regular field
          fields.push({
            value: fieldName,
            label: displayName,
            fieldName,
            dataType: fieldConfig.dataType || 'string',
            fieldConfig
          });
        }
        
        // Check if this is a custom field stored in Attributes
        if (fieldConfig.storage?.type === 'attributes') {
          // Already added above as regular field, so no action needed
        }
      }
    });
    
    // Sort by label
    return fields.sort((a, b) => a.label.localeCompare(b.label));
  }, [entityConfig, i18n?.language, customerType, entityName]);

  // Get operators based on data type
  const getOperatorsForField = (field) => {
    if (!field) return [];
    
    const dataType = field.dataType || 'string';
    
    switch (dataType) {
      case 'integer':
      case 'number':
      case 'decimal':
        return [
          { value: 'equals', label: t('filter.operator.equals', 'Equals') },
          { value: 'notEquals', label: t('filter.operator.notEquals', 'Not Equals') },
          { value: 'greaterThan', label: t('filter.operator.greaterThan', 'Greater Than') },
          { value: 'greaterThanOrEqual', label: t('filter.operator.greaterThanOrEqual', 'Greater Than Or Equal') },
          { value: 'lessThan', label: t('filter.operator.lessThan', 'Less Than') },
          { value: 'lessThanOrEqual', label: t('filter.operator.lessThanOrEqual', 'Less Than Or Equal') },
          { value: 'isNull', label: t('filter.operator.isNull', 'Is Empty') },
          { value: 'isNotNull', label: t('filter.operator.isNotNull', 'Is Not Empty') }
        ];
      case 'date':
      case 'datetime':
        return [
          { value: 'equals', label: t('filter.operator.equals', 'Equals') },
          { value: 'notEquals', label: t('filter.operator.notEquals', 'Not Equals') },
          { value: 'greaterThan', label: t('filter.operator.after', 'After') },
          { value: 'lessThan', label: t('filter.operator.before', 'Before') },
          { value: 'isNull', label: t('filter.operator.isNull', 'Is Empty') },
          { value: 'isNotNull', label: t('filter.operator.isNotNull', 'Is Not Empty') }
        ];
      case 'boolean':
        return [
          { value: 'equals', label: t('filter.operator.equals', 'Equals') },
          { value: 'notEquals', label: t('filter.operator.notEquals', 'Not Equals') }
        ];
      default: // string, text, etc.
        return [
          { value: 'equals', label: t('filter.operator.equals', 'Equals') },
          { value: 'notEquals', label: t('filter.operator.notEquals', 'Not Equals') },
          { value: 'contains', label: t('filter.operator.contains', 'Contains') },
          { value: 'notContains', label: t('filter.operator.notContains', 'Does Not Contain') },
          { value: 'startsWith', label: t('filter.operator.startsWith', 'Starts With') },
          { value: 'endsWith', label: t('filter.operator.endsWith', 'Ends With') },
          { value: 'isNull', label: t('filter.operator.isNull', 'Is Empty') },
          { value: 'isNotNull', label: t('filter.operator.isNotNull', 'Is Not Empty') }
        ];
    }
  };

  // Get field options for select/choice fields
  const getFieldOptions = (field) => {
    if (!field || !field.fieldConfig) return [];
    
    // Check if it's a select field
    if (field.fieldConfig.ui?.inputType === 'select' || 
        field.fieldConfig.ui?.inputType === 'choice') {
      return getFieldOptionsSync(entityConfig, field.fieldName);
    }
    
    return [];
  };

  // Add new filter
  const handleAddFilter = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    isUpdatingRef.current = true;
    const newFilter = {
      id: Date.now().toString(),
      field: filterableFields[0]?.value || '',
      operator: 'equals',
      value: ''
    };
    const updated = [...localFilters, newFilter];
    setLocalFilters(updated);
    // Keep all filters in state (including incomplete ones), but only send valid ones to parent
    const validFilters = updated.filter(f => {
      if (!f.field || !f.operator) return false;
      if (!operatorRequiresValue(f.operator)) return true; // isNull/isNotNull don't need values
      // For operators that require values, check that value is not empty/null/undefined
      return f.value !== undefined && f.value !== null && f.value !== '';
    });
    console.log('🔍 EntityFilter: handleAddFilter - validFilters:', validFilters);
    onFiltersChange(validFilters);
    // Reset flag after state update
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  // Remove filter
  const handleRemoveFilter = (filterId) => {
    isUpdatingRef.current = true;
    const updated = localFilters.filter(f => f.id !== filterId);
    setLocalFilters(updated);
    const validFilters = updated.filter(f => 
      f.field && f.operator && (operatorRequiresValue(f.operator) ? (f.value !== undefined && f.value !== '') : true)
    );
    onFiltersChange(validFilters);
    // Reset flag after state update
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  // Update filter
  const handleFilterChange = (filterId, key, value) => {
    isUpdatingRef.current = true;
    const updated = localFilters.map(filter => {
      if (filter.id === filterId) {
        const newFilter = { ...filter, [key]: value };
        
        // If field changed, reset operator and value
        if (key === 'field') {
          const selectedField = filterableFields.find(f => f.value === value);
          const operators = getOperatorsForField(selectedField);
          newFilter.operator = operators[0]?.value || 'equals';
          newFilter.value = '';
        }
        
        // If operator changed to isNull/isNotNull, clear value
        if (key === 'operator' && !operatorRequiresValue(value)) {
          newFilter.value = '';
        }
        
        return newFilter;
      }
      return filter;
    });
    setLocalFilters(updated);
    // Always update local state, but only send valid filters to parent
    const validFilters = updated.filter(f => {
      if (!f.field || !f.operator) return false;
      if (!operatorRequiresValue(f.operator)) return true; // isNull/isNotNull don't need values
      // For operators that require values, check that value is not empty/null/undefined
      return f.value !== undefined && f.value !== null && f.value !== '';
    });
    console.log('🔍 EntityFilter: handleFilterChange - validFilters:', validFilters);
    onFiltersChange(validFilters);
    // Reset flag after state update
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  // Clear all filters
  const handleClearAll = () => {
    isUpdatingRef.current = true;
    setLocalFilters([]);
    onFiltersChange([]);
    // Reset flag after state update
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  };

  // Get selected field object
  const getSelectedField = (fieldValue) => {
    return filterableFields.find(f => f.value === fieldValue);
  };

  // Fetch distinct values for a field
  const fetchFieldValues = async (field) => {
    if (!field || !field.fieldName) {
      console.warn('Cannot fetch values: field or fieldName is missing', field);
      setAvailableValues([]);
      return;
    }
    
    setLoadingValues(true);
    setAvailableValues([]); // Clear previous values
    
    try {
      // Get tenant code from localStorage - try multiple keys for compatibility
      let tenantCode = localStorage.getItem('selectedTenantCode');
      if (!tenantCode) {
        const selectedTenant = localStorage.getItem('selectedTenant');
        if (selectedTenant) {
          try {
            const tenant = JSON.parse(selectedTenant);
            tenantCode = tenant.repositoryCode || tenant.tenant_code || tenant.code;
          } catch (e) {
            tenantCode = selectedTenant;
          }
        }
      }
      if (!tenantCode) {
        tenantCode = localStorage.getItem('tenantCode') || '';
      }
      
      const selectedRole = localStorage.getItem('selectedRole');
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      if (tenantCode) {
        headers['X-Tenant-Code'] = tenantCode;
      }
      if (selectedRole) {
        headers['X-Selected-Role'] = selectedRole;
      }
      
      const params = new URLSearchParams({
        ...(customerType && { customerType })
      });
      
      // Use customer-specific endpoint for customer entity
      // For table column fields, we need to send the full field name (fieldName.columnKey)
      let fieldNameToSend = field.fieldName;
      if (field.isTableColumn && field.columnKey) {
        fieldNameToSend = `${field.fieldName}.${field.columnKey}`;
      }
      
      // Encode field name in case it contains dots (for table fields like "fieldname.column")
      const encodedFieldName = encodeURIComponent(fieldNameToSend);
      const endpoint = entityName === 'customer' 
        ? `/api/customers/fields/${encodedFieldName}/values?${params}`
        : `/api/entities/${entityName}/fields/${encodedFieldName}/values?${params}`;
      
      console.log('Fetching field values:', { 
        endpoint, 
        fieldName: fieldNameToSend, 
        isTableColumn: field.isTableColumn,
        columnKey: field.columnKey,
        tenantCode,
        fieldConfig: field.fieldConfig ? {
          storageType: field.fieldConfig.storage?.type,
          isCustomAttribute: field.fieldConfig.metadata?.isCustomAttribute
        } : null
      });
      
      const response = await fetch(endpoint, {
        headers
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to fetch values');
      }
      
      const data = await response.json();
      const values = data.values || [];
      console.log('Received field values:', { fieldName: field.fieldName, count: values.length });
      setAvailableValues(values);
    } catch (error) {
      console.error('Error fetching field values:', error);
      setAvailableValues([]);
      // Show error message to user
      if (showValueSelector) {
        // Error will be shown in the modal
      }
    } finally {
      setLoadingValues(false);
    }
  };

  // Handle value selector button click
  const handleShowValueSelector = (filterId, field) => {
    if (!field || !field.fieldName) {
      console.warn('Cannot show value selector: field or fieldName is missing', field);
      return;
    }
    
    // Allow for string, text, select/choice fields, and undefined dataType (defaults to text)
    const isTextField = !field.dataType || 
                       field.dataType === 'string' || 
                       field.dataType === 'text' ||
                       field.fieldConfig?.ui?.inputType === 'select' ||
                       field.fieldConfig?.ui?.inputType === 'choice' ||
                       (!['integer', 'number', 'decimal', 'date', 'datetime', 'boolean'].includes(field.dataType));
    
    if (!isTextField) {
      console.warn('Value selector only available for text and select fields', field);
      return;
    }
    
    setShowValueSelector({ filterId, field });
    fetchFieldValues(field);
  };

  // Handle value selection from popup
  const handleSelectValue = (value) => {
    if (showValueSelector) {
      handleFilterChange(showValueSelector.filterId, 'value', value);
      setShowValueSelector(null);
      setAvailableValues([]);
    }
  };

  // Render value input based on field type
  const renderValueInput = (filter) => {
    const field = getSelectedField(filter.field);
    if (!field) {
      return (
        <div className="flex-1 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
          {t('filter.selectFieldFirst', 'Please select a field first')}
        </div>
      );
    }

    const operators = getOperatorsForField(field);
    const currentOperator = operators.find(op => op.value === filter.operator);
    
    // If operator doesn't require value
    if (!operatorRequiresValue(filter.operator)) {
      return null;
    }

    // Date/datetime input
    if (field.dataType === 'date' || field.dataType === 'datetime') {
      const inputType = field.dataType === 'date' ? 'date' : 'datetime-local';
      return (
        <input
          type={inputType}
          value={filter.value || ''}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      );
    }

    // Boolean input
    if (field.dataType === 'boolean') {
      return (
        <select
          value={filter.value || ''}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('filter.selectValue', 'Select value...')}</option>
          <option value="true">{t('common.yes', 'Yes')}</option>
          <option value="false">{t('common.no', 'No')}</option>
        </select>
      );
    }

    // Number input
    if (['integer', 'number', 'decimal'].includes(field.dataType)) {
      return (
        <input
          type="number"
          value={filter.value || ''}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
          step={field.dataType === 'decimal' ? '0.01' : '1'}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      );
    }

    // Default: text input with value selector button (for string, text, select/choice, and any other text-like types)
    // Show value selector for all string/text/select fields
    const isTextField = !field.dataType || 
                       field.dataType === 'string' || 
                       field.dataType === 'text' ||
                       field.fieldConfig?.ui?.inputType === 'select' ||
                       field.fieldConfig?.ui?.inputType === 'choice' ||
                       (!['integer', 'number', 'decimal', 'date', 'datetime', 'boolean'].includes(field.dataType));
    
    return (
      <div className="flex-1 flex items-center gap-2">
        <input
          type="text"
          value={filter.value || ''}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
          placeholder={t('filter.enterValue', 'Enter value...')}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
        {isTextField && field && field.fieldName && (
          <button
            type="button"
            onClick={() => {
              if (!field || !field.fieldName) {
                console.warn('Cannot show value selector: field or fieldName is missing', field);
                return;
              }
              handleShowValueSelector(filter.id, field);
            }}
            className="px-2 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title={t('filter.selectExistingValue', 'Select from existing values')}
          >
            <List className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  if (!entityConfig) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        {t('filter.loading', 'Loading filter options...')}
      </div>
    );
  }

  const hasActiveFilters = localFilters.length > 0;

  return (
    <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
      hasActiveFilters
        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddFilter(e);
            }}
            className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30"
          >
            <Plus className="h-3 w-3" />
            <span>{t('filter.addFilter', 'Add Filter')}</span>
          </button>
          <Filter className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            {t('filter.advancedFilters', 'Advanced Filters')}
          </h3>
          {hasActiveFilters && (
            <span className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-0.5 rounded-full text-xs font-medium">
              {localFilters.length}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
            >
              {t('filter.clearAll', 'Clear All')}
            </button>
          )}
        </div>
      </div>

      {/* Filter Rules */}
      {localFilters.length === 0 ? (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
          {t('filter.noFilters', 'No filters applied. Click "Add Filter" to start filtering.')}
        </div>
      ) : (
        <div className="space-y-2">
          {localFilters.map((filter, index) => {
            const field = getSelectedField(filter.field);
            const operators = field ? getOperatorsForField(field) : [];
            
            return (
              <div
                key={filter.id}
                className="flex flex-col sm:flex-row gap-2 p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                {/* Field Selector */}
                <select
                  value={filter.field || ''}
                  onChange={(e) => handleFilterChange(filter.id, 'field', e.target.value)}
                  className="flex-1 sm:flex-none sm:w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('filter.selectField', 'Select field...')}</option>
                  {filterableFields.map(field => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                {/* Operator Selector */}
                {field && (
                  <select
                    value={filter.operator || 'equals'}
                    onChange={(e) => handleFilterChange(filter.id, 'operator', e.target.value)}
                    className="flex-1 sm:flex-none sm:w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                )}

                {/* Value Input */}
                {field && operatorRequiresValue(filter.operator) && (
                  <div className="flex-1">
                    {renderValueInput(filter)}
                  </div>
                )}

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => handleRemoveFilter(filter.id)}
                  className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title={t('filter.removeFilter', 'Remove filter')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Value Selector Modal */}
      {showValueSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowValueSelector(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('filter.selectValue', 'Select Value')} - {showValueSelector.field?.label || showValueSelector.field?.fieldName}
              </h3>
              <button
                onClick={() => setShowValueSelector(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {loadingValues ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-2"></div>
                  <p>{t('filter.loadingValues', 'Loading values...')}</p>
                </div>
              ) : availableValues.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p className="mb-2 font-medium">{t('filter.noValuesFound', 'No values found')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {t('filter.noValuesHint', 'This field has no existing values in the database. You can still type a value manually.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2">
                    {t('filter.selectValueFromList', 'Select a value from the list below:')} ({availableValues.length} {t('filter.values', 'values')})
                  </p>
                  {availableValues.map((value, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectValue(value)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      {value || <span className="italic text-gray-400">(Empty)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityFilter;

